import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import MaterialIcon from "../../../components/MaterialIcon";
import { $crud } from "../../../helper/CrudFactory";
import PackageTile from "../../../components/PackageTile";
import { SET_SUBSCRIPTION_STATUS } from "../../../store";
import {
  confirmSetup,
  fetchAndStoreSubscriptionStatus,
  getKjuupIoFeatures,
  getPlans,
  isBillingNetworkError,
  labelForKjuupIoFeature,
  normalizeSubscriptionStatus,
  startTrial,
  subscribe,
} from "../../../services/PaymentService";
import {
  CheckoutForm,
  DEFAULT_ANNUAL_SAVE_PCT,
  DEFAULT_TRIAL_DAYS,
  DiscountedPrice,
  PLAN_MONTHLY,
  PLAN_YEARLY,
  applyDiscount,
  derivePricing,
  discountSummaryText,
  listPlan,
  persistPreferredPlan,
  priceMajor,
  t,
  tNode,
  yearlyEquivalentMajor,
} from "../../Subscription/SubscriptionPage";
import { Card, Skeleton } from "./CockpitPrimitives";
import { OfferChoice, OfferFeatures } from "./CockpitOfferParts";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Kjuup.io booking directly on the plain Kjuup cockpit: the same offer as the
 * subscription page (plan, price, Admin Center features, trial terms). The
 * trial starts, or the Stripe checkout opens, right here.
 *
 * `locked`: Kjuup.io is not enabled for this user in the Admin Panel yet. The
 * offer is shown as a preview; the CTA is greyed out and `onRequest` opens the
 * request page instead of starting a trial or checkout.
 */
export default function CockpitKjuupIoOffer({ strings, language, canStartTrial: canStartTrialProp, onActivated, locked = false, onRequest }) {
  // A locked user cannot have used a trial yet, so the preview shows the trial offer.
  const canStartTrial = canStartTrialProp || locked;
  const dispatch = useDispatch();
  const subscription = useSelector((s) => s.subscription) || {};
  const [listPriceByPlan, setListPriceByPlan] = useState({});
  const [featureRows, setFeatureRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(PLAN_YEARLY);
  const [processing, setProcessing] = useState(false);
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getPlans().catch(() => []), getKjuupIoFeatures().catch(() => [])]).then(([plans, features]) => {
      if (cancelled) return;
      const byKey = {};
      (Array.isArray(plans) ? plans : []).forEach((p) => {
        if (p && p.key) byKey[p.key] = p;
      });
      setListPriceByPlan(byKey);
      setFeatureRows(Array.isArray(features) ? features : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const features = useMemo(() => {
    const fromAdmin = featureRows.map((f) => labelForKjuupIoFeature(f, language)).filter(Boolean);
    if (fromAdmin.length) return fromAdmin;
    return [
      strings.kjuup_io_feature_email || "Email integration",
      strings.kjuup_io_feature_ai || "AI-powered smart replies",
      strings.kjuup_io_feature_tasks || "Tasks and appointments synced with calendar",
    ].filter(Boolean);
  }, [featureRows, language, strings]);

  const discount = subscription.discount || null;
  const discountPercent = Number(discount && discount.percent) || 0;
  const priceByPlan = useMemo(() => applyDiscount(listPriceByPlan, discountPercent), [listPriceByPlan, discountPercent]);
  const discountText = discountSummaryText(strings, discount);
  const savePct = priceByPlan?.[PLAN_YEARLY]?.savePercent;
  const pricing = derivePricing(priceByPlan, selectedPlan, savePct > 0 ? savePct : DEFAULT_ANNUAL_SAVE_PCT);
  const isYearly = pricing.billingPeriod === "yearly";
  const displayPrice = isYearly ? pricing.yearlyEquivalent : pricing.monthlyPrice;
  const shownList = listPlan(priceByPlan[isYearly ? PLAN_YEARLY : PLAN_MONTHLY]);
  const listDisplay = shownList ? (isYearly ? yearlyEquivalentMajor(shownList) : priceMajor(shownList)) : null;
  const priceLine = displayPrice
    ? listDisplay
      ? tNode(strings, "kjuup_io_price_month_line", { price: <DiscountedPrice net={displayPrice} list={listDisplay} /> }, "{price} / month")
      : t(strings, "kjuup_io_price_month_line", { price: displayPrice }, "{price} / month")
    : "—";

  const selectPlan = (key) => {
    setSelectedPlan(key);
    persistPreferredPlan(key);
  };

  const errorText = (e, fallback) =>
    isBillingNetworkError(e)
      ? strings.kjuup_io_request_timeout || "The connection timed out. Please try again."
      : (e && e.message) || fallback;

  const handleStartTrial = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const data = await startTrial();
      if (data) {
        dispatch({ type: SET_SUBSCRIPTION_STATUS, payload: normalizeSubscriptionStatus(data) });
      } else {
        await fetchAndStoreSubscriptionStatus(dispatch);
      }
      $crud.notify({ type: "success", message: strings.kjuup_io_toast_trial_started || "Your free trial has started" });
      if (onActivated) onActivated();
    } catch (e) {
      $crud.notify({ type: "error", message: errorText(e, strings.kjuup_io_toast_trial_fail || strings.something_went_wrong) });
    } finally {
      setProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const data = await subscribe(selectedPlan);
      const { clientSecret, intentType, publishableKey, billingStartsAfterTrial, setupIntentId, subscriptionId } = data || {};
      if (!clientSecret || !publishableKey) {
        throw new Error(strings.something_went_wrong || "Something went wrong");
      }
      setCheckout({
        clientSecret,
        intentType: intentType || "payment",
        stripePromise: loadStripe(publishableKey),
        billingStartsAfterTrial: !!billingStartsAfterTrial || intentType === "setup",
        setupIntentId: setupIntentId || null,
        subscriptionId: subscriptionId || null,
      });
    } catch (e) {
      $crud.notify({ type: "error", message: errorText(e, strings.something_went_wrong) });
    } finally {
      setProcessing(false);
    }
  };

  const waitForStatus = async (ok) => {
    for (let i = 0; i < 5; i += 1) {
      const data = await fetchAndStoreSubscriptionStatus(dispatch);
      if (ok(data)) return true;
      await sleep(1500);
    }
    return false;
  };

  // Same outcome handling as the subscription page.
  const handleCheckoutSuccess = async () => {
    if (checkout.billingStartsAfterTrial || checkout.intentType === "setup") {
      let confirmed = false;
      if (checkout.setupIntentId) {
        try {
          const status = await confirmSetup({ setupIntentId: checkout.setupIntentId, subscriptionId: checkout.subscriptionId });
          if (status) dispatch({ type: SET_SUBSCRIPTION_STATUS, payload: normalizeSubscriptionStatus(status) });
          confirmed = true;
        } catch (_) {
          /* poll below */
        }
      }
      if (!confirmed) await waitForStatus((d) => d && d.hasPaymentMethod);
      $crud.notify({
        type: "success",
        message: strings.kjuup_io_toast_card_saved || "Payment method saved. You'll be charged when the trial ends.",
      });
      await fetchAndStoreSubscriptionStatus(dispatch);
    } else {
      $crud.notify({ type: "success", message: strings.kjuup_io_toast_activating });
      const active = await waitForStatus((d) => d && d.active && !d.trialing);
      if (!active) $crud.notify({ type: "success", message: strings.kjuup_io_toast_activating_soon });
    }
    setCheckout(null);
    setProcessing(false);
    if (onActivated) onActivated();
  };

  const planOptions = [
    { key: PLAN_MONTHLY, label: strings.kjuup_io_plan_monthly || "Monthly" },
    { key: PLAN_YEARLY, label: strings.kjuup_io_plan_yearly || "Yearly" },
  ];

  return (
    <>
      <Card className="cockpit-offer cockpit-offer--io">
        <div className="cockpit-offer-pitch">
          <div className="cockpit-offer-brand">
            <PackageTile pkg="io" size="sm" />
            <span className="cockpit-eyebrow cockpit-eyebrow--accent">Kjuup.io</span>
            {locked ? (
              <span className="cockpit-offer-badge cockpit-offer-badge--soon">{strings.products_coming_soon || "Coming soon"}</span>
            ) : canStartTrial ? (
              <span className="cockpit-offer-badge">{strings.kjuup_io_trial_badge || "1 MONTH FREE TRIAL"}</span>
            ) : null}
          </div>
          <h3 className="cockpit-upsell-title">
            {canStartTrial
              ? strings.cockpit_upsell_io_title || "Dein Büro, das mitdenkt"
              : strings.kjuup_io_trial_ended_title || "Your free trial has ended"}
          </h3>
          <p className="cockpit-upsell-text">
            {canStartTrial
              ? strings.cockpit_upsell_io_text ||
                "Verbinde deine E-Mail-Konten mit Kjuup. E-Mails, Anhänge und Nachrichten werden automatisch im passenden Projekt gespeichert und können optional auf deinem Server abgelegt werden. Täglich bekommst du eine Zusammenfassung aller Projekte mit den wichtigsten Punkten aus allen Inhalten, und deine Aufgaben synchronisieren sich mit deinem Kalender oder deiner To-do-App."
              : strings.kjuup_io_trial_ended_body || "Subscribe to Kjuup.io to keep using email and other premium features."}
          </p>
          <OfferFeatures
            title={strings.kjuup_io_features_title || "What you get:"}
            features={features}
            loading={loading}
          />
        </div>

        <div className={`cockpit-offer-checkout${locked ? " cockpit-offer-checkout--soon" : ""}`}>
          {locked ? (
            // Not bookable yet: say so first; plan and price below are only a muted preview.
            <div className="cockpit-offer-soon" role="status">
              <MaterialIcon icon="schedule" size={20} color="currentColor" weight={400} />
              <div className="cockpit-offer-soon-body">
                <div className="cockpit-offer-soon-title">{strings.cockpit_upsell_io_soon_title || "Not bookable yet"}</div>
                <p className="cockpit-offer-soon-text">
                  {strings.cockpit_upsell_io_soon_text ||
                    "Kjuup.io is launching soon. Request information now – we will let you know as soon as you can book Kjuup.io."}
                </p>
              </div>
            </div>
          ) : null}
          <div className={locked ? "cockpit-offer-preview" : "cockpit-offer-pricing"}>
            {locked ? (
              <div className="cockpit-offer-preview-caption">{strings.cockpit_upsell_io_planned_price || "Planned price"}</div>
            ) : null}
            <OfferChoice
              label={strings.kjuup_io_choose_plan || "Choose your plan"}
              ariaLabel={strings.kjuup_io_billing_period_aria || "Billing period"}
              options={planOptions}
              value={selectedPlan}
              onChange={selectPlan}
            />
            <div aria-live="polite">
              <div className="cockpit-offer-price">
                {loading ? <Skeleton height={22} width={110} /> : <span className="cockpit-offer-price-main">{priceLine}</span>}
                {isYearly && pricing.yearlySavings ? (
                  <span className="cockpit-offer-save">
                    {t(strings, "kjuup_io_save_amount_year", { price: pricing.yearlySavings }, "Save {price} per year")}
                  </span>
                ) : null}
              </div>
              {isYearly && pricing.yearlyPrice ? (
                <div className="cockpit-offer-sub">
                  {t(strings, "kjuup_io_billed_annually_amount", { price: pricing.yearlyPrice }, "{price} billed annually")}
                </div>
              ) : null}
              {discountText ? <div className="cockpit-offer-sub cockpit-offer-sub--accent">{discountText}</div> : null}
            </div>
          </div>
          {canStartTrial && !locked ? (
            <ul className="cockpit-offer-terms">
              <li>
                {strings.kjuup_io_info_no_card || "No credit card required – start immediately without entering payment details."}
              </li>
              <li>
                {t(
                  strings,
                  "kjuup_io_info_days_free",
                  { days: subscription.trialPeriodDays || DEFAULT_TRIAL_DAYS },
                  "{days} days completely free – your subscription only starts after the trial."
                )}
              </li>
              <li>{strings.kjuup_io_info_cancel_anytime || "Cancel anytime – no commitment, no risk."}</li>
            </ul>
          ) : null}
          {locked ? (
            <button type="button" className="cockpit-offer-cta" onClick={onRequest}>
              {strings.cockpit_upsell_io_cta_request || "Request information"}
            </button>
          ) : (
            <button
              type="button"
              className="cockpit-offer-cta"
              disabled={processing}
              onClick={canStartTrial ? handleStartTrial : handleSubscribe}
            >
              {processing
                ? "…"
                : canStartTrial
                  ? strings.kjuup_io_start_free_trial || "Start free now – no risk"
                  : strings.kjuup_io_subscribe_now || "Subscribe now"}
            </button>
          )}
          {locked ? null : (
            <p className="cockpit-offer-note">
              {canStartTrial ? (
                strings.kjuup_io_trial_trust ||
                "No payment today. No credit card details are requested before your paid subscription starts."
              ) : (
                <>
                  <MaterialIcon icon="lock" size={14} color="currentColor" weight={400} />
                  <span>{strings.kjuup_io_secured_stripe || "Secured by Stripe · cancel anytime"}</span>
                </>
              )}
            </p>
          )}
        </div>
      </Card>

      {checkout ? (
        <div className="kjuup-sub-modal-overlay">
          <div className="kjuup-sub-modal-card">
            <div className="kjuup-sub-modal-title">{strings.kjuup_io_subscribe_now || "Subscribe now"}</div>
            <Elements
              stripe={checkout.stripePromise}
              options={{
                clientSecret: checkout.clientSecret,
                appearance: { theme: "stripe", variables: { colorPrimary: "#e24bf5" } },
              }}
            >
              <CheckoutForm
                intentType={checkout.intentType}
                onSuccess={handleCheckoutSuccess}
                onCancel={() => setCheckout(null)}
                strings={strings}
                processing={processing}
                setProcessing={setProcessing}
                ctaLabel={strings.kjuup_io_subscribe_now || "Subscribe now"}
              />
            </Elements>
          </div>
        </div>
      ) : null}
    </>
  );
}
